<template>
<div :class="$style.block">
  <figure :class="$style.container">
    <figcaption :class="$style.textContent">
      <TextBlock
        :content="content.text"
        :reversed="true"
        :title="content.title" />
      <ul data-anim="staggerVue">
        <li
          v-for="(slide, i) in content.slides"
          :key="slide.text"
          :class="[
            currentIndex === i ? $style.active : null,
            $style.hoverText
          ]"
          >
          <span
            @mouseenter="mouseenter(i)"
            @mouseleave="mouseleave"
            v-html="slide.text"
          />
        </li>
      </ul>
    </figcaption>

    <div :class="$style.pictureCont">
      <transition-group
        :class="$style.picture"
        tag="ul"
        @enter="onSlideEnter"
        @leave="onSlideLeave"
      >
        <li
          v-for="(slide, i) in content.slides"
          :key="slide.src"
          v-show="currentIndex === i"
          :style="`background-image: url(${slide.src})`"
        ></li>
      </transition-group>
      <div :class="$style.arrowContainer">
        <div :class="$style.rightArrow">
          <ArrowButton :action="nextSlide" :direction="'right'"></ArrowButton>
        </div>
        <div :class="$style.leftArrow">
          <ArrowButton :action="prevSlide" :direction="'left'"></ArrowButton>
        </div>
      </div>
    </div>
  </figure>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import gsap from 'gsap'
import TextBlock from '@/components/TextBlock'
import ArrowButton from '@/components/ArrowButton'
// import Hammer from 'hammerjs'

export default {
  props: ['content', 'settings'],

  components: {
    TextBlock,
    ArrowButton
  },

  data() {
    return {
      currentIndex: 0,
      interval: null,
      direction: 'next'
    }
  },

  computed: {
    ...mapGetters(['getScrollbar'])
  },

  mounted() {
    this.$nextTick(() => {
      this.getScrollbar.addListener(this.customScrollbar.bind(this))
    })

    // this.hm = new Hammer.Manager(this.$el)
    // const swipe = new Hammer.Swipe()
    // this.hm.add(swipe)
    // this.hm.on('swiperight', this.nextSlide)
  },

  methods: {
    customScrollbar() {
      const visible = this.getScrollbar.isVisible(this.$el)

      if (visible && !this.isPlaying) {
        this.addInterval()
      } else if (!visible && this.interval) {
        this.clearInterval()
      }
    },

    mouseenter(i) {
      this.currentIndex = i
      this.clearInterval()
    },

    mouseleave() {
      this.addInterval()
    },

    addInterval() {
      this.isPlaying = true
      this.interval = setInterval(this.incrementIndex, 3500)
    },

    clearInterval() {
      this.isPlaying = false
      window.clearInterval(this.interval)
      this.interval = null
    },

    incrementIndex() {
      this.direction = 'next'
      this.currentIndex = this.currentIndex === (this.content.slides.length - 1) ? 0 :
        this.currentIndex + 1
    },

    onSlideEnter(el, done) {
      const xPercent = this.direction === 'next' ? -100 : 100

      gsap.fromTo(el, 0.8, {
        autoAlpha: 1,
        xPercent,
        zIndex: 2
      }, {
        scale: 1,
        autoAlpha: 1,
        xPercent: 0,
        ease: gsap.Expo.easeInOut,
        onComplete: done
      })
    },

    onSlideLeave(el, done) {
      gsap.to(el, 1, {
        autoAlpha: 0,
        zIndex: 1,
        onComplete: done
      })
    },

    nextSlide() {
      this.direction = 'next'
      this.currentIndex = this.currentIndex === (this.content.slides.length - 1) ? 0 :
        this.currentIndex + 1

      this.clearInterval()
      this.addInterval()
    },

    prevSlide() {
      this.direction = 'prev'
      this.currentIndex = this.currentIndex === 0 ? this.content.slides.length - 1 :
        this.currentIndex - 1

      this.clearInterval()
      this.addInterval()
    }
  }
}
</script>

<style module lang="scss">
@import "~sass-mq/mq";

.block {
  position: relative;
}

.container {
  @include grid-size();
  display: flex;
  padding: 0;
  flex-flow: column;
  padding: $big-margin-mobile;

  @include mq(sm) {
    flex-flow: row-reverse;
    justify-content: space-between;
    padding: 0px;
    padding-right: $big-margin;
  }
}

.textContent {
  max-width: 350px;
  width: 100%;
  display: flex;
  padding: 0;
  flex-flow: column;
  justify-content: center;
  margin-bottom: $standard-margin-mobile;

  @include mq(sm) {
    padding-left: $standard-margin;
    margin-bottom: 0;
  }

  ul {
    @include mq(sm) {
      padding-left: $standard-margin;
    }
  }
}

.hoverText {
  padding: 5px 0;
  cursor: pointer;
  color: #3C3C3B;

  span {
    display: inline-block;
    transition: all 0.3s ease-out;

    :global(.no-touchevents) &:hover {
      color: $color-blue;
    }
  }

  &.active {
    color: $color-light;
  }
}

.pictureCont {
  position: relative;
  flex: 0 0 auto;
  background: $color-dark;

  @include mq(sm) {
    width: 60%;
  }

  .arrowContainer {
    position: absolute;
    top: 50%;
    right: -30px;
    z-index: 20;
    transform: translateY(-50%);

    @include mq(sm) {
      right: -60px;
    }

    @include mq(ds) {
      right: -85px;
    }

    .rightArrow {
      margin-bottom: 20px;
    }
  }
}

.picture {
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    display: block;
    width: 100%;
    padding-bottom: 90%;
  }

  > li {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-size: cover;
    background-position: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/HoverSlideshow.vue